/**
 * useCampaignFilter - Hook global para filtro de campanhas.
 *
 * Mantém a lista de campanhas disponíveis (mock por enquanto, futuramente do backend)
 * e o conjunto de IDs selecionados. Persiste em localStorage e usa pattern de listeners
 * para sincronizar entre componentes (mesma abordagem de useViewMode).
 *
 * `selectedIds = null` significa "todas selecionadas" (estado inicial e desejável).
 * Quando o usuário clica em uma campanha específica, transitamos para um array.
 */

import { useEffect, useState, useCallback } from 'react'

const STORAGE_KEY = 'deebs:selectedCampaigns'

// Mock de campanhas — fallback até o backend carregar
const DEFAULT_CAMPAIGNS = [
  { id: 'c01', name: 'Black Friday 2026', source: 'Meta Ads', status: 'active' },
  { id: 'c02', name: 'Demo Request - Maio', source: 'Google Ads', status: 'active' },
  { id: 'c03', name: 'Brand Search BR', source: 'Google Ads', status: 'active' },
  { id: 'c04', name: 'Lookalike Compradores', source: 'Meta Ads', status: 'active' },
  { id: 'c05', name: 'Retargeting Site', source: 'Meta Ads', status: 'active' },
  { id: 'c06', name: 'Concorrentes - Search', source: 'Google Ads', status: 'paused' },
  { id: 'c07', name: 'Performance Max', source: 'Google Ads', status: 'active' },
  { id: 'c08', name: 'Interesse - B2B SaaS', source: 'Meta Ads', status: 'active' },
  { id: 'c09', name: 'YouTube Awareness', source: 'Google Ads', status: 'paused' },
  { id: 'c10', name: 'Reels Engagement', source: 'Meta Ads', status: 'active' },
  { id: 'c11', name: 'LinkedIn Lead Gen', source: 'LinkedIn', status: 'active' },
  { id: 'c12', name: 'Webinar Conversions', source: 'Meta Ads', status: 'paused' }
]

// Singleton: campanhas disponíveis podem ser injetadas pelo backend
let _availableCampaigns = [...DEFAULT_CAMPAIGNS]

export function setAvailableCampaigns(campaigns) {
  _availableCampaigns = campaigns && campaigns.length > 0 ? campaigns : [...DEFAULT_CAMPAIGNS]
}

function getAvailableCampaigns() {
  return _availableCampaigns
}

const listeners = new Set()

function readStored() {
  if (typeof window === 'undefined') return null
  const raw = window.localStorage.getItem(STORAGE_KEY)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : null
  } catch {
    return null
  }
}

function writeStored(value) {
  if (typeof window === 'undefined') return
  if (value === null) {
    window.localStorage.removeItem(STORAGE_KEY)
  } else {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value))
  }
}

function notify(value) {
  listeners.forEach((fn) => fn(value))
}

export function useCampaignFilter() {
  const [selectedIds, setSelectedIdsState] = useState(readStored)

  useEffect(() => {
    const fn = (next) => setSelectedIdsState(next)
    listeners.add(fn)
    return () => listeners.delete(fn)
  }, [])

  const available = getAvailableCampaigns()
  const isAllSelected = selectedIds === null || selectedIds.length === available.length

  // IDs efetivamente em uso (sempre array — null vira "todos")
  const effectiveIds = isAllSelected
    ? available.map((c) => c.id)
    : selectedIds

  const setSelectedIds = useCallback((next) => {
    // null OR array com tamanho = total → normaliza para null (estado "todas")
    const currentAvailable = getAvailableCampaigns()
    const value = next === null || (Array.isArray(next) && next.length === currentAvailable.length)
      ? null
      : next
    writeStored(value)
    setSelectedIdsState(value)
    notify(value)
  }, [])

  const toggle = useCallback((id) => {
    const currentAvailable = getAvailableCampaigns()
    const current = readStored() ?? currentAvailable.map((c) => c.id)
    const next = current.includes(id)
      ? current.filter((x) => x !== id)
      : [...current, id]
    setSelectedIds(next.length === 0 ? [] : next)
  }, [setSelectedIds])

  const selectAll = useCallback(() => setSelectedIds(null), [setSelectedIds])
  const clearAll = useCallback(() => setSelectedIds([]), [setSelectedIds])

  const count = isAllSelected ? available.length : (selectedIds?.length ?? 0)
  const total = available.length

  // Label compacto para o botão da topbar
  let label
  if (isAllSelected) {
    label = 'Todas as campanhas'
  } else if (count === 0) {
    label = 'Nenhuma campanha'
  } else if (count === 1) {
    const currentAvailable = getAvailableCampaigns()
    const camp = currentAvailable.find((c) => c.id === selectedIds[0])
    label = camp?.name || '1 campanha'
  } else {
    label = `${count} campanhas`
  }

  return {
    available,
    selectedIds: effectiveIds,
    isAllSelected,
    isFiltered: !isAllSelected,
    count,
    total,
    label,
    setSelectedIds,
    toggle,
    selectAll,
    clearAll
  }
}
